import { InlineEditCard, type SchemaField, type InlineEditField } from '../organisms';
import { Badge } from '../atoms';
import { latestAttr, type AttrMap } from '../lib/inlineAttribution';
import { formatDate, isDateFieldName, isIsoDateString, isYearFieldName } from '../lib/orgTimezone';
import { useUserNames, isUserReference } from '../lib/useUserNames';

/**
 * detailFields — shared building blocks for schema-driven entity-detail
 * "Overview" surfaces.
 *
 * Extracted from GenericDetail so the flat GenericDetail page and the tabbed
 * GovernmentEntityDetail page render field data through ONE implementation
 * (type-aware formatting, section grouping, InfoCard/Row markup). One concern,
 * one source of truth — per the CLAUDE.md standard.
 */

/**
 * Singularize a plural title or table name for a header prefix / modal title
 * ("Assessors" → "Assessor", "Properties" → "Property").
 *
 * ⚠️ Stripping a trailing `s` is NOT enough, and the failure is user-visible: it
 * renders "Propertie", "Addresse", "Ledger Entrie", "Import Batche". Eight of this
 * schema's tables end in `-ies`, `-sses` or `-ches`, so the naive rule is wrong on
 * every one of them. The order below matters — `-ies` and the sibilant `-es` cases
 * must be tested before the bare `-s`, and a word already ending in `-ss`
 * ("address") is ALREADY singular and must be left alone rather than clipped to
 * "addres".
 */
export function singularize(title: string): string {
  if (/[^aeiou]ies$/i.test(title)) return title.slice(0, -3) + 'y';   // Properties → Property
  if (/(ss|sh|ch|x|z)es$/i.test(title)) return title.slice(0, -2);    // Addresses → Address
  if (/(ss|us)$/i.test(title)) return title;                          // Address / Status — already singular
  return title.endsWith('s') ? title.slice(0, -1) : title;            // Assessors → Assessor
}

/** Type-aware render of a field value (boolean → badge, enum → label, date → org tz, …). */
export function formatValue(field: SchemaField, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (field.dataType === 'boolean') {
    return value ? <Badge variant="success" dot>Yes</Badge> : <span style={{ color: 'var(--color-ink-3)' }}>No</span>;
  }
  if (field.enumOptions && Array.isArray(field.enumOptions)) {
    const opt = field.enumOptions.find(o => o.value === value);
    return opt?.label ?? String(value);
  }
  if (field.dataType === 'number' || field.dataType === 'integer') {
    // Year fields render bare (2026), never with a thousands separator (2,026) — §5.2.
    if (isYearFieldName(field.name)) return String(value);
    return typeof value === 'number' ? value.toLocaleString() : String(value);
  }
  // Dates & timestamps → the org-timezone formatter. Detect by declared type,
  // by column name (a _date/_at suffix, last_*, or a domain date keyword), and
  // by ISO-string shape — so a raw UTC ISO never falls through to String(value).
  if (
    field.dataType === 'timestamp' ||
    field.dataType === 'date' ||
    isDateFieldName(field.name) ||
    isIsoDateString(value)
  ) {
    return formatDate(value);
  }
  return String(value);
}

/** Visible, ordered detail fields: drop hidden + system columns, sort by displayOrder. */
export function visibleDetailFields(fields: SchemaField[]): SchemaField[] {
  return fields
    .filter(f => !f.hidden && !['organization_id', 'created_by_user_id'].includes(f.name))
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

/** Group ordered fields by `section` (falling back to "Details"). */
export function groupFieldsBySection(fields: SchemaField[]): Map<string, SchemaField[]> {
  const sections = new Map<string, SchemaField[]>();
  for (const f of fields) {
    const key = f.section ?? 'Details';
    if (!sections.has(key)) sections.set(key, []);
    sections.get(key)!.push(f);
  }
  return sections;
}

export function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

export function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0',
      fontSize: 'var(--fs-sm)',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{
        color: 'var(--color-ink)',
        fontWeight: 'var(--fw-medium)',
        textAlign: 'right',
        wordBreak: 'break-word',
        maxWidth: '60%',
      }}>{value}</span>
    </div>
  );
}

/**
 * DetailFieldGrid — the schema-driven Overview surface: all visible fields
 * grouped by section into a responsive grid of InfoCards. Takes the RAW schema
 * fields + the record row; filtering, ordering, and grouping happen internally
 * so both GenericDetail and GovernmentEntityDetail render identically.
 */
export function DetailFieldGrid({ fields, row }: { fields: SchemaField[]; row: Record<string, unknown> }) {
  const sections = groupFieldsBySection(visibleDetailFields(fields));
  // A `*_user_id` column (or any FK to users) is a person: render the full name, never the
  // identifier string (PRD 2026-09-16, applied to every schema-driven detail at once).
  const users = useUserNames();
  const valueFor = (field: SchemaField, value: unknown) =>
    isUserReference(field.name, field.fk?.table) && value ? users.nameOf(value) : formatValue(field, value);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
      gap: 'var(--sp-4)',
    }}>
      {Array.from(sections.entries()).map(([sectionLabel, sectionFields]) => (
        <InfoCard key={sectionLabel} label={sectionLabel}>
          {sectionFields.map(field => (
            <Row key={field.name} label={field.label} value={valueFor(field, row[field.name])} />
          ))}
        </InfoCard>
      ))}
    </div>
  );
}

// System-maintained audit columns: shown for context but never operator-editable
// (the write path stamps them). Timestamps are also caught by dataType below.
const SYSTEM_READONLY_FIELDS = new Set(['updated_by', 'updated_at', 'created_by', 'created_by_user_id', 'created_at']);

/** Map a schema field to an InlineEditCard field (type, options, immutability).
 *  Exported for unit testing the type/read-only mapping. */
export function toInlineField(f: SchemaField): InlineEditField {
  const base = {
    key: f.name,
    label: f.label,
    required: f.required,
    display: (v: unknown) => formatValue(f, v),
  };
  // Read-only in the inline grid: FK columns (there is no FK picker here — editing a
  // raw id as free text could clear/repoint the link; the whole-record "Edit all
  // fields" modal edits FKs properly with fkOptions), plus immutable / primary-key /
  // system timestamp + audit columns.
  if (f.fk || f.immutable || f.pk || f.dataType === 'timestamp' || SYSTEM_READONLY_FIELDS.has(f.name)) return { ...base, type: 'text', readOnly: true };
  // D — universal field-format rules, applied to every schema-driven editable block:
  // state is always a dropdown, postal code is 5 digits numbers-only, phone is the
  // standard format. Matched by column name so it sweeps every profile at once.
  if (/(^|_)state$/.test(f.name)) return { ...base, type: 'state' };
  if (/(zip|postal)/.test(f.name)) return { ...base, type: 'postal' };
  if (/phone/.test(f.name) && !/_type$/.test(f.name)) return { ...base, type: 'phone' };
  if (f.dataType === 'boolean') return { ...base, type: 'checkbox' };
  if (f.enumOptions && f.enumOptions.length > 0) return { ...base, type: 'select', options: f.enumOptions };
  if (f.dataType === 'number' || f.dataType === 'integer') return { ...base, type: 'number' };
  if (f.dataType === 'date') return { ...base, type: 'date' };
  return { ...base, type: 'text' };
}

/**
 * InlineDetailFieldGrid — the inline-editable Overview surface (A12). Same schema
 * fields, section grouping, and type-aware formatting as DetailFieldGrid, but each
 * section is an InlineEditCard you edit in place (with its own field attribution)
 * rather than the read-only InfoCard/Row grid. Immutable + system columns render
 * read-only inside their card. Used by the government entity profiles; the flat
 * GenericDetail keeps the read-only DetailFieldGrid.
 */
export function InlineDetailFieldGrid({
  fields, row, routePath, recordId, invalidateKeys, attr, canEdit = true,
}: {
  fields: SchemaField[];
  row: Record<string, unknown>;
  routePath: string;
  recordId: string;
  invalidateKeys: unknown[][];
  attr: AttrMap;
  canEdit?: boolean;
}) {
  const sections = groupFieldsBySection(visibleDetailFields(fields));
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
      gap: 'var(--sp-4)',
    }}>
      {Array.from(sections.entries()).map(([sectionLabel, sectionFields]) => (
        <InlineEditCard
          key={sectionLabel}
          title={sectionLabel}
          routePath={routePath}
          recordId={recordId}
          record={row}
          canEdit={canEdit}
          invalidateKeys={invalidateKeys}
          attribution={latestAttr(attr, sectionFields.map(f => f.name))}
          fields={sectionFields.map(toInlineField)}
        />
      ))}
    </div>
  );
}
